/*!
 * Copyright 2020 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {KeyValue} from "@angular/common";
// eslint-disable-next-line max-len
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  TrackByFunction,
} from "@angular/core";
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {generateUuid} from "@app/models/functions/generate-uuid.function";
import {AlertController} from "@ionic/angular";
import {
  ClinicalDataType,
  TrecAssumption,
  TrecAssumptionUnit,
  TrecTherapyAssumptionData,
} from "@trec/api/clinical-data";
import {TherapyTemplate} from "@trec/api/drug-store";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {Subscription} from "rxjs";
import {TherapyCycleInfo} from "../../../models/therapy-cycle-info";

@Component({
  selector: "c3-therapy-variable-assumptions-table",
  templateUrl: "./therapy-variable-assumptions-table.component.html",
  styleUrls: ["./therapy-variable-assumptions-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TherapyVariableAssumptionsTableComponent implements OnDestroy {
  // IO ///////////////////////////////////////////////////////////////////////////////////////////

  private _formAssumptions: UntypedFormArray;
  @Input()
  public get formAssumptions(): UntypedFormArray {
    return this._formAssumptions;
  }
  public set formAssumptions(value: UntypedFormArray) {
    this._formAssumptions = value;

    this.formAssumptionSubscr?.unsubscribe();
    this.formAssumptionSubscr = this.formAssumptions.valueChanges.subscribe(() => {
      this.updateCycleView(false);
    });
    this.onSelectDay(null);
    this.updateCycleView(true);
  }

  @Input()
  assumptionUnit?: TrecAssumptionUnit | null;

  private _cycleInfo?: TherapyCycleInfo | null | undefined;
  @Input()
  public get cycleInfo(): TherapyCycleInfo | null | undefined {
    return this._cycleInfo;
  }
  public set cycleInfo(value: TherapyCycleInfo | null | undefined) {
    this._cycleInfo = value;
    this.startPadding = this.cycleInfo?.startPadding
      ? this.cycleInfo.startPadding % this.cycleInfo.duration
      : 0;
    this.updateCycleView(true);
  }

  startPadding = 0;

  /**
   * Maximum number of days that can be added to the schema.
   */
  @Input()
  maxDays?: number | null;

  @Input()
  therapyStartDay?: string | null;

  @Input()
  therapyTemplate?: TherapyTemplate;

  // Constants ////////////////////////////////////////////////////////////////////////////////////

  readonly DAY_MOMENTS: {[key: string]: string};

  /**
   * Function used to sort entries by value, alphabetically.
   */
  readonly sortAlphabetically = (a: KeyValue<string, string>, b: KeyValue<string, string>) =>
    a.value > b.value ? 1 : -1;
  readonly trackByValue: TrackByFunction<KeyValue<string, string>> = (_i, item) => item.key;

  // Cycle data ///////////////////////////////////////////////////////////////////////////////////

  currentDayIndex: number | null = null;
  currentCycleIndexes: (number | null)[] = [];
  currentCycle = 0;

  // Bulk edit ////////////////////////////////////////////////////////////////////////////////////

  bulkEditApplyAlsoToPauseDays = false;

  private formAssumptionSubscr?: Subscription;

  readonly ClinicalDataType = ClinicalDataType;
  readonly trackByAssumption: TrackByFunction<UntypedFormGroup> = (_i, item) => item.get("_id");

  constructor(
    private fb: UntypedFormBuilder,
    private alertCtrl: AlertController,
    private cd: ChangeDetectorRef,
    private c3Snackbar: C3SnackbarStatusService,
    private zone: NgZone,
    @Inject(LOCALE_ID) private localeId: string
  ) {
    // Init list of moments (exclude "at_need")
    const {["at_need"]: _, ...moments} = TrecAssumption.TRANSLATION[this.localeId]["moment"];
    this.DAY_MOMENTS = moments;
  }

  onSelectDay(dayIndex: number | null) {
    this.currentDayIndex = dayIndex;
  }

  onAddAssumption() {
    this.formAssumptions.push(this.fb.group({assumptions: this.fb.array([])}));

    // Set new assumption as current selected one
    this.onSelectDay(this.formAssumptions.length - 1);
  }

  async onRemoveAssumption(dayIndex: number) {
    const alert = await this.alertCtrl.create({
      header: `Rimuovi ${dayIndex + 1}° giorno`,
      // eslint-disable-next-line max-len
      message: `Tutti i giorni successivi al giorno ${dayIndex + 1
        } verranno anticipati di un giorno (verranno spostati a sinistra nella tabella).<br>Se si desidera invece cancellare tutte le assunzioni presenti per il giorno e programmare un giorno di pausa, è necessario selezionare il giorno e sucessivamente rimuovere le assunzioni dalla lista sottostante la tabella.<br>Vuoi rimuovere il giorno dalla programmazione?`,
      buttons: [
        {text: "Annulla", role: "cancel"},
        {
          text: "Procedi",

          handler: () => {
            this.formAssumptions.removeAt(dayIndex);
            this.formAssumptions.markAsDirty();
            this.cd.markForCheck();
          },
        },
      ],
    });

    await alert.present();
  }

  onAddDailyAssumption(dayIndex: number) {
    if (this.assumptionUnit) {
      const dailyAssumptions = this.getDailyAssumptions(dayIndex);

      dailyAssumptions?.push(this.createAssumptionControl());
      dailyAssumptions?.markAsDirty();
    } else {
      this.c3Snackbar.showInfo({
        message: "Prima è necessario definire la modalità di assunzione del farmaco",
      });
    }
  }

  onRemoveDailyAssumption(dayIndex: number, assumptionIndex: number) {
    const dailyAssumptions = this.getDailyAssumptions(dayIndex);
    dailyAssumptions?.removeAt(assumptionIndex);
    dailyAssumptions?.markAsDirty();
    this.cd.markForCheck();
  }

  onPrevCycle() {
    if (this.cycleInfo) {
      this.currentCycle = Math.max(0, this.currentCycle - 1);

      this.onSelectDay(null);
      this.updateCycleView(false);
    }
  }

  onNextCycle() {
    if (this.cycleInfo) {
      const repetitions = Math.max(0, this.cycleInfo.repeat);
      this.currentCycle = Math.min(this.currentCycle + 1, repetitions - 1);

      this.onSelectDay(null);
      this.updateCycleView(false);
    }
  }

  async onBulkEdit(options: {fromIndex: number; type: "to-cycle-end" | "to-therapy-end"}) {
    this.zone.runOutsideAngular(async () => {
      // Get requested assumptions config
      const sampleAssumptionsControl: UntypedFormArray = this.formAssumptions
        .at(options.fromIndex)
        ?.get("assumptions") as UntypedFormArray;

      switch (options.type) {
        case "to-cycle-end":
          // Get end of the cycle
          const toIndex = this.currentCycleIndexes[this.currentCycleIndexes.length - 1];

          if (toIndex === null) {
            throw new Error(
              `Something goes wrong. Fail to get "to" index in bulk edit of variable assumptions. Index is null`
            );
          }

          await (
            await this.alertCtrl.create({
              header: `Applica modifica`,
              // eslint-disable-next-line max-len
              message: `A tutti i giorni successivi al giorno ${options.fromIndex
                } <strong>presenti nel ciclo corrente</strong> ${this.bulkEditApplyAlsoToPauseDays &&
                "(compresi i giorni per cui non è prevista alcuna assunzione)"
                } verrà applicato lo stesso schema di assunzione. Procedere?`,
              buttons: [
                {text: "Annulla", role: "cancel"},
                {
                  text: "Procedi",
                  handler: () => {
                    this.applySampleAssumptions({
                      from: options.fromIndex,
                      to: toIndex,
                      sampleAssumptionsControl,
                    });
                    this.cd.markForCheck();
                  },
                },
              ],
            })
          ).present();
          break;

        case "to-therapy-end":
          await (
            await this.alertCtrl.create({
              header: `Applica modifica`,
              // eslint-disable-next-line max-len
              message: `A tutti i giorni successivi al giorno ${options.fromIndex} ${this.bulkEditApplyAlsoToPauseDays &&
                "(compresi i giorni per cui non è prevista alcuna assunzione)"
                } verrà applicato lo stesso schema di assunzione. Procedere?`,
              buttons: [
                {text: "Annulla", role: "cancel"},
                {
                  text: "Procedi",
                  handler: () => {
                    this.applySampleAssumptions({
                      from: options.fromIndex,
                      sampleAssumptionsControl,
                    });
                    this.cd.markForCheck();
                  },
                },
              ],
            })
          ).present();
          break;

        default:
          // Do nothing
          break;
      }
    });
  }

  private applySampleAssumptions(options: {
    from: number;
    to?: number;
    sampleAssumptionsControl: UntypedFormArray;
  }) {
    const end = options.to ? options.to + 1 : this.formAssumptions.length;

    for (let i = options.from; i < end; i++) {
      // Re-create control
      let newControls: UntypedFormGroup[];

      const assControl = this.formAssumptions.at(i)?.get("assumptions") as UntypedFormArray;

      // Check if pause days should not be edited and day is empty -> do not edit
      if (!this.bulkEditApplyAlsoToPauseDays && assControl.controls.length === 0) {
        newControls = []; // Keep an empty array
      } else {
        // Create new control
        newControls = options.sampleAssumptionsControl.controls.map((aC) =>
          this.createAssumptionControl(aC.value)
        );
      }

      // Apply new controls
      assControl.clear();
      newControls.forEach((c) => assControl.push(c));
      assControl.markAsDirty();
    }
  }

  private updateCycleView(resetCurrentCycle: boolean) {
    this.zone.runOutsideAngular(() => {
      if (!this.cycleInfo) {
        // No cycles -> no need to wrap assumption list
        this.currentCycle = 0;
        this.currentCycleIndexes = this.formAssumptions.controls.map((_c, i) => i);
      } else {
        // Cycles! Wrap assumption list and show only part of the view

        if (resetCurrentCycle) {
          this.currentCycle = 0;
        }

        const cycleStartIndex = this.currentCycle * this.cycleInfo.duration;
        const startPaddingArray = this.startPadding ? new Array(this.startPadding).fill(null) : [];

        this.currentCycleIndexes = [...startPaddingArray, ...this.formAssumptions.controls]
          .slice(cycleStartIndex, cycleStartIndex + this.cycleInfo.duration) // Keep only controls that must be in view
          // Map to indexes of current view
          .map((c, i) => (c === null ? null : cycleStartIndex + i));
      }

      this.cd.markForCheck();
    });
  }

  private getDailyAssumptions(dayIndex: number): UntypedFormArray | null {
    return (this.formAssumptions.at(dayIndex)?.get("assumptions") as UntypedFormArray) ?? null;
  }

  private createAssumptionControl(assumption?: TrecTherapyAssumptionData): UntypedFormGroup {
    return this.fb.group({
      _id: [generateUuid(), Validators.required],
      quantity: [assumption?.quantity ?? 1, [Validators.required, Validators.min(0)]],
      unit: [assumption?.unit ?? this.assumptionUnit, Validators.required],
      time: [assumption?.time ?? "08:00", Validators.required],
      moment: [assumption?.moment ?? undefined],
    });
  }

  ngOnDestroy() {
    this.formAssumptionSubscr?.unsubscribe();
  }
}
